// src/components/FlowCard.js
import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
  useTheme,
  CircularProgress,
  Grid,
  alpha
} from '@mui/material';
import { motion } from 'framer-motion';
import TimelineIcon from '@mui/icons-material/Timeline';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EventIcon from '@mui/icons-material/Event';
import CampaignIcon from '@mui/icons-material/Campaign';
import DescriptionIcon from '@mui/icons-material/Description';
import { designTokens, commonCardStylesDark } from '../../../theme/designTokens';
import { bg, text as textColors, border, shadow, overlay } from '../../../utils/darkModeAdapter';

// 円形プログレスコンポーネント
function CircularProgressWithLabel({ value, size = 120, color, label, icon: Icon }) {
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        {/* 背景円 */}
        <CircularProgress
          variant="determinate"
          value={100}
          size={size}
          thickness={4}
          sx={{
            color: alpha(color, 0.15),
            position: 'absolute',
          }}
        />
        {/* 進捗円 */}
        <CircularProgress
          variant="determinate"
          value={value}
          size={size}
          thickness={4}
          sx={{
            color: color,
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            },
          }}
        />
        {/* 中央のアイコンと数値 */}
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {Icon && (
            <Icon
              sx={{
                fontSize: 28,
                color: color,
                mb: 0.5,
              }}
            />
          )}
          <Typography
            variant="h5"
            component="div"
            sx={{
              fontWeight: 700,
              color: color,
            }}
          >
            {`${Math.round(value)}%`}
          </Typography>
        </Box>
      </Box>
      <Typography
        variant="body2"
        sx={{
          mt: 1.5,
          fontWeight: 600,
          color: 'text.primary',
          textAlign: 'center',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

// 統計カードコンポーネント
function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Box
      component={motion.div}
      whileHover={{ y: -2 }}
      transition={{ duration: 0.15 }}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 2.5,
        borderRadius: 2,
        backgroundColor: alpha(color, 0.08),
        border: `1px solid ${alpha(color, 0.15)}`,
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          backgroundColor: alpha(color, 0.15),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 1.5,
        }}
      >
        <Icon sx={{ fontSize: 24, color }} />
      </Box>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          color: 'text.secondary',
          mb: 0.5,
          textAlign: 'center',
        }}
      >
        {label}
      </Typography>
      <Typography
        component={motion.div}
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        variant="h4"
        sx={{
          fontWeight: 700,
          color: color,
          lineHeight: 1,
        }}
      >
        {value}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
        }}
      >
        件
      </Typography>
    </Box>
  );
}

function FlowCard() {
  const BACKEND_URL = process.env.REACT_APP_API_URL;
  const theme = useTheme();
  const [flowData, setFlowData] = useState({
    recruitPlanProgress: 0,
    mediaSelectionProgress: 0,
    totalJobPosts: 0,
    totalInterviews: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 機能別カラー
  const recruitPlanColor = designTokens.featureThemes.recruit_plan.primary;
  const mediaSelectionColor = designTokens.featureThemes.media_selection.primary;
  const jobPostColor = designTokens.featureThemes.job_posting.primary;
  const interviewColor = designTokens.featureThemes.interview_support.primary;

  useEffect(() => {
    const fetchFlowProgress = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/recruit_plan_assistant/flow_progress`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error('Failed to fetch flow progress');
        }

        const data = await response.json();
        setFlowData({
          recruitPlanProgress: data.recruit_plan_progress,
          mediaSelectionProgress: data.media_selection_progress,
          totalJobPosts: data.total_job_posts,
          totalInterviews: data.total_interviews
        });
      } catch (err) {
        console.error('Error fetching flow progress:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchFlowProgress();
  }, [BACKEND_URL]);

  return (
    <Card
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      sx={{
        ...commonCardStylesDark(theme),
        mb: 3,
        backgroundColor: `${designTokens.colors.primary}0D`,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* タイトル */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5,
            }}
          >
            <TimelineIcon
              sx={{
                fontSize: 24,
                color: theme.palette.primary.main,
              }}
            />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
              }}
            >
              採用フロー全体
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              各プロセスの進捗状況
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ p: 2, textAlign: 'center' }}>
            データの取得に失敗しました
          </Typography>
        ) : (
          <>
            {/* 進捗円グラフ */}
            <Grid container spacing={4} sx={{ mb: 4 }}>
              <Grid
                item
                xs={12}
                sm={6}
                component={motion.div}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <CircularProgressWithLabel
                  value={flowData.recruitPlanProgress}
                  color={recruitPlanColor}
                  label="採用計画 進捗"
                  icon={AssignmentIcon}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                component={motion.div}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <CircularProgressWithLabel
                  value={flowData.mediaSelectionProgress}
                  color={mediaSelectionColor}
                  label="募集媒体 進捗"
                  icon={CampaignIcon}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* 統計カード */}
            <Grid container spacing={2}>
              <Grid
                item
                xs={12}
                sm={6}
                component={motion.div}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <StatCard
                  icon={DescriptionIcon}
                  label="求人票"
                  value={flowData.totalJobPosts}
                  color={jobPostColor}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                component={motion.div}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <StatCard
                  icon={EventIcon}
                  label="面接予定"
                  value={flowData.totalInterviews}
                  color={interviewColor}
                />
              </Grid>
            </Grid>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default FlowCard;
